<template>
    <modal name="feedbackGridModal" class="feedbackGridModal" :height="'auto'" :width="'70%'">
        <div class="feedback-grid-wrapper">
            <h3 class="title">{{ $t('feedbacks_modal_title') }}</h3>
            <p class="subtitle">{{ $t('feedbacks_modal_subtitle') }}</p>

            <div v-if="loading" class="grid-loading">{{ $t('feedbacks_modal_loading') }}</div>
            <div v-else>
                <div class="feedback-table-container">
                    <table v-if="formattedFeedbacks.length" class="feedback-table">
                        <thead>
                            <tr>
                                <th>{{ $t('date') }}</th>
                                <th>{{ $t('justification') }}</th>
                                <th>{{ $t('actions') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in formattedFeedbacks" :key="item.id">
                                <td>{{ item.date }}</td>
                                <td class="just-text">{{ item.justificativa }}</td>
                                <td class="actions">
                                    <button type="button" class="action-button" @click="handleEdit(item)">
                                        <i class="fas fa-pencil"></i>
                                    </button>
                                    <button type="button" class="action-button" @click="requestDelete(item)">
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                    <div v-else class="empty-state">{{ $t('no_feedback_found') }}</div>
                </div>
            </div>


            <div class="button-div feedback-button-div">
                <button type="button" class="button ghost" @click="$modal.hide('feedbackGridModal')">
                    <i class="fas fa-times"></i> {{ $t('Fechar') }}
                </button>
            </div>
        </div>
        <ConfirmModal ref="confirmModal" :message="confirmMessage" :confirm-text="confirmText" :cancel-text="cancelText"
            modal-name="feedbackConfirmModal" @confirm="confirmDelete" @cancel="cancelDelete" />
    </modal>
</template>

<script>
import PlatesService from "@/application/services/Plates";
import moment from "moment";
import ConfirmModal from "./ConfirmModal.vue";

export default {
    name: "FeedbackGridModal",
    components: { ConfirmModal },
    props: {
        scenarioId: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            feedbacks: [],
            loading: false,
            rowPendingDelete: null,
            confirmMessage: "Deseja realmente excluir esse feedback?",
            confirmText: "Sim",
            cancelText: "Não",
        };
    },
    computed: {
        formattedFeedbacks() {
            return (this.feedbacks || []).map((item) => ({
                ...item,
                id: item.NRFEEDBACKCARD,
                date: this.formatDate(item.DTINCLUSAO),
                justificativa: item.JUSTIFICATIVA || "",
            }));
        },
    },
    methods: {
        getDateSeparatorText() {
            return `[${this.$t('at')}]`;
        },
        formatDate(value) {
            return value ? moment(value, ["DD/MM/YYYY HH:mm:ss"]).format(`DD/MM/YYYY ${this.getDateSeparatorText()} HH:mm:ss`) : "";
        },
        async loadFeedbacks() {
            if (this.scenarioId === null || this.scenarioId === undefined) {
                this.feedbacks = [];
                return;
            }
            this.loading = true;
            try {
                const data = await PlatesService.getFeedbacks(this.scenarioId);
                this.feedbacks = data || [];
            } catch (e) {
                this.feedbacks = [];
            } finally {
                this.loading = false;
            }
        },
        handleEdit(row) {
            this.$emit("edit", row);
        },
        requestDelete(row) {
            this.rowPendingDelete = row;
            if (this.$refs.confirmModal) {
                this.$refs.confirmModal.open();
            }
        },
        confirmDelete() {
            if (!this.rowPendingDelete) return;
            this.$emit("delete", this.rowPendingDelete);
            this.feedbacks = this.feedbacks.filter((item) => (item.NRFEEDBACKCARD) !== (this.rowPendingDelete.NRFEEDBACKCARD));
            this.rowPendingDelete = null;
        },
        cancelDelete() {
            this.rowPendingDelete = null;
        },
    },
    watch: {
        scenarioId() {
            this.loadFeedbacks();
        },
    },
};
</script>

<style scoped lang="less">
@import (reference) url("../../../assets/variables.less");

.feedback-grid-wrapper {
    width: calc(100% - 40px);
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    // max-height: 60dvh;
    // overflow-y: auto;
}

.title {
    margin: 0;
}

.subtitle {
    margin: 0;
    color: #555;
}

.grid-loading,
.empty-state {
    padding: 12px;
    background: #f7f7f7;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
}

.feedback-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: auto;

    th,
    td {
        padding: 10px 12px;
        border-bottom: 1px solid #e7e7e7;
        text-align: left;
        vertical-align: top;
    }

    td:first-child {
        width: clamp(200px, 20%, 300px);
    }

    th:last-child,
    td:last-child {
        text-align: right;
        white-space: nowrap;
        width: 1%;
    }

    th {
        background: #212146;
        font-weight: 600;
        min-width: -webkit-fill-available;
        color: white;
        position: sticky;
    }

    /* Linhas ímpares (primeira, terceira, quinta, etc.) */
    tbody tr:nth-child(odd) {
        background-color: #e7e7e7;
    }

    /* Linhas pares (segunda, quarta, sexta, etc.) */
    tbody tr:nth-child(even) {
        background-color: #ffffff;
    }

    /* Efeito hover nas linhas */
    tbody tr:hover {
        background-color: #e8f4f8;
    }

    .action-button {
        font-weight: 500;
        padding: 12px 16px;
        font-size: 1.1rem;
        box-shadow: 0 2px 2px rgba(0, 0, 0, 0.24), 0 0 2px rgba(0, 0, 0, 0.12);

        color: #dc143d;
        border: none;

        cursor: pointer;

        border-radius: 4px;

        &:hover {
            background: #dc143d;
            color: white;
        }
    }

    .just-text {
        white-space: nowrap;
        word-break: break-word;
        max-width: 40dvw;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

.feedback-table-container {
    max-height: 55dvh;
    overflow-y: auto;
    border-radius: 6px;
    overflow-x: hidden;
    border: 1px solid #e7e7e7;

}

.feedback-table {
    width: 100%;
    border-collapse: collapse;
}

.feedback-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
}

.actions {
    display: flex;
    gap: 8px;
    flex-wrap: nowrap;
    justify-content: flex-start;
}

.button {
    min-width: 120px;
    padding: 6px 12px;
    margin: 0;
}

.ghost {
    background-color: white;
    box-shadow: none;
    color: #dc143d;
    border: 1px solid #dc143d;

    &:hover {
        color: #212146;
        border: 1px solid #212146;
    }
}

.danger {
    background: #dc143d;
    color: #fff;
    border: 1px solid #dc143d;

    &:hover {
        background: #b01030;
        border-color: #b01030;
    }
}

.feedback-button-div {
    justify-content: flex-end;
}
</style>
